import {
  Alert,
  Box,
  Container,
  Modal,
  Link,
  Header,
  SpaceBetween,
  Button,
  Table,
} from '@amzn/awsui-components-core';
import {
  CodeLink,
  ContentType,
  CodeRepoReviewStatus,
  ReviewStatus,
  RepoType,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState } from 'react';
import CodeRepoItem from './CodeRepoItem';
import constants from '../../../common/constants';
import AddCodeRepoModalButton from './code-repo/AddCodeRepoModalButton';
import EditCodeRepoModalButton from './code-repo/EditCodeRepoModalButton';
import { codeRepoReviewStatusLabels } from './code-repo/CodeRepoReviewStatus';
import TutorialLink from '../../content/TutorialLink';
import { ContentSchemaSection } from '../../../model/ContentSchemaSection';

interface Props {
  codeLinks: CodeLink[];
  userType: string;
  isAllowedToSeeAppSecLink: boolean;
  handleAddLink: (codeLink: CodeLink) => void;
  readOnly?: boolean;
  allowedRepoType?: RepoType;
}

const CodeRepoList: React.FunctionComponent<Props> = (props: Props) => {
  const {
    codeLinks,
    handleAddLink,
    readOnly,
    userType,
    isAllowedToSeeAppSecLink,
    allowedRepoType,
  } = props;
  // @ts-expect-error TS2345 - strict migration
  const [codeLink, setCodeLink] = useState<CodeLink>(codeLinks?.[0] || null);
  const [isAddRepoModalVisible, setShowAddRepoModal] = useState<boolean>(false);
  const [isDeleteMoalVisible, setShowDeleteModal] = useState<boolean>(false);

  const showDeleteCodeModal = () => setShowDeleteModal(true);
  const hideDeleteCodeModal = () => setShowDeleteModal(false);

  const handleAddCodeRepo = (codeLinkToAdd: CodeLink) => {
    if (handleAddLink) {
      handleAddLink(codeLinkToAdd);
      setCodeLink(codeLinkToAdd);
    }
  };

  const handleDeleteCodeRepo = () => {
    if (handleAddLink) {
      handleAddLink({
        url: '',
        label: '',
      });
    }
    // @ts-expect-error TS2345 - strict migration
    setCodeLink(null);
    setShowDeleteModal(false);
  };

  const codeRepoColumnDefinitions = [
    {
      id: 'codeTitle',
      header: 'Title',
      // @ts-expect-error TS7006 - strict migration
      cell: (item) => (
        <Link external href={item.url}>
          {item.label}
        </Link>
      ),
    },
    // @ts-expect-error TS7006 - strict migration
    { id: 'codeBranch', header: 'Branch', cell: (item) => item.branch || '-' },
    // @ts-expect-error TS7006 - strict migration
    { id: 'codeType', header: 'Type', cell: (item) => item.repoType },
    {
      id: 'codeAppsec',
      header: 'AppSec',
      // @ts-expect-error TS7006 - strict migration
      cell: (item) =>
        item.appsecTicket ? (
          <Link external href={item.appsecTicket}>
            {codeRepoReviewStatusLabels.get(item.appsecStatus)}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      id: 'codeOpenSource',
      header: 'Open source',
      // @ts-expect-error TS7006 - strict migration
      cell: (item) =>
        item.openSourceTicket ? (
          <Link external href={item.openSourceTicket}>
            {codeRepoReviewStatusLabels.get(item.openSourceStatus)}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      id: 'codeAction',
      header: '',
      cell: (item: CodeLink) => (
        <Box float="right">
          <EditCodeRepoModalButton
            addRepoToContent={handleAddCodeRepo}
            repoData={item}
            // @ts-expect-error TS2322 - strict migration
            allowedRepoType={allowedRepoType}
          />
          <Button
            onClick={showDeleteCodeModal}
            iconUrl="/images/icon-delete.svg"
            variant="icon"
            ariaLabel="Delete repository"
            iconAlt="Delete"
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    // @ts-expect-error TS2345 - strict migration
    setCodeLink(codeLinks?.[0] || null);
  }, [codeLinks]);

  const renderColumnView = () => {
    if (!codeLink) {
      return null;
    }
    if (codeLink.repoType === RepoType.Internal && userType.toLocaleLowerCase() !== 'aws') {
      // only show internal link to AWS employees
      return null;
    }
    // Keeping ReviewStatus for backward compatibility. New repos will use CodeRepoReviewStatus
    const approvedTicketStatus = [
      ReviewStatus.Approved as string,
      CodeRepoReviewStatus.ResolvedSuccessful as string,
    ];
    if (
      codeLink.repoType === RepoType.External &&
      !isAllowedToSeeAppSecLink &&
      // @ts-expect-error TS2345 - strict migration
      (!approvedTicketStatus.includes(codeLink.appsecStatus) ||
        // @ts-expect-error TS2345 - strict migration
        !approvedTicketStatus.includes(codeLink.openSourceStatus))
    ) {
      // only show external link when appsec review and open source review is approved
      // tech writers can still see the link details
      return null;
    }
    return (
      <article id="code-links">
        <header>
          <Box variant="h5">Repository</Box>
        </header>
        <CodeRepoItem codeLink={codeLink} isAllowedToSeeAppSecLink={isAllowedToSeeAppSecLink} />
      </article>
    );
  };

  const addRepoButton = (
    <AddCodeRepoModalButton
      addRepoToContent={handleAddCodeRepo}
      disabled={!!codeLink}
      // @ts-expect-error TS2322 - strict migration
      allowedRepoType={allowedRepoType}
    />
  );

  const renderCodeRepoInfo = () => {
    if (!codeLink) {
      return (
        <Box textAlign="center" padding="xxl">
          There are currently no repositories
        </Box>
      );
    }
    return <Table items={props.codeLinks} columnDefinitions={codeRepoColumnDefinitions} />;
  };

  const deleteCodeModalFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={hideDeleteCodeModal}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleDeleteCodeRepo}>
          Delete repository
        </Button>
      </SpaceBetween>
    </Box>
  );

  const repoAccessWarning = (
    <Alert type={'warning'}>Please grant the required access permissions for your Code Repo</Alert>
  );

  const codeRepoListTabView = (
    <div>
      <Box margin={{ bottom: 'l' }}>
        <Container
          disableContentPaddings={!!codeLink}
          header={
            <Header
              variant="h3"
              actions={addRepoButton}
              info={
                <TutorialLink
                  contentType={ContentType.Pattern}
                  schemaSection={ContentSchemaSection.ContentTypeMetadata}
                  sectionId={'codeLinks'}
                />
              }
            >
              {constants.CODE_REPO_HEADER_TEXT}
            </Header>
          }
        >
          {renderCodeRepoInfo()}
        </Container>
        <Modal
          visible={isDeleteMoalVisible}
          onDismiss={hideDeleteCodeModal}
          header="Delete repository"
          footer={deleteCodeModalFooter}
          closeAriaLabel="Close modal"
        >
          Are you sure you want to delete the code repository?
        </Modal>
      </Box>
      {repoAccessWarning}
    </div>
  );
  return !!readOnly ? renderColumnView() : codeRepoListTabView;
};

export default CodeRepoList;
